import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { DATA_LEVELS, MODULE_LEVELS, type Permissions } from "@kickwin/shared";
import { api, type RoleTemplate } from "@/lib/api";
import { useCan, useMe } from "@/lib/shell-context";
import { revealSoon } from "@/lib/utils";
import { PermissionMatrix } from "@/components/permission-matrix";
import { useConfirm } from "@/components/confirm";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Panel,
  Status,
  Table,
  Td,
  Th,
} from "@/components/primitives";

/**
 * Roles (decision 42). A role is a template of permissions that admins are
 * given; changing it changes everyone who has it. Someone who is not the
 * owner can only build roles within what they hold themselves.
 */

/** A new role starts with nothing, and the owner ticks up from there. */
const NOTHING: Permissions = {
  modules: Object.fromEntries(
    Object.entries(MODULE_LEVELS).map(([m, levels]) => [m, levels[0]]),
  ) as Permissions["modules"],
  data: Object.fromEntries(
    Object.entries(DATA_LEVELS).map(([f, levels]) => [f, levels[0]]),
  ) as Permissions["data"],
};

interface Draft {
  id: number | null;
  name: string;
  description: string;
  permissions: Permissions;
  isBuiltin: boolean;
}

export function RolesPage() {
  const me = useMe();
  const canManage = useCan("admins", "manage");
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api.get<RoleTemplate[]>("/roles") });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["roles"] });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        name: d.name.trim(),
        description: d.description.trim() || null,
        permissions: d.permissions,
      };
      return d.id === null ? api.post("/roles", body) : api.patch(`/roles/${d.id}`, body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/roles/${id}`),
    onSuccess: invalidate,
  });

  const open = (d: Draft) => {
    setDraft(d);
    revealSoon("role-editor");
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {draft && (
        <Panel
          id="role-editor"
          title={draft.id === null ? "New role" : `Edit “${draft.name}”`}
          description="Changes apply to everyone with this role, the next time they load a page."
        >
          <form
            className="grid gap-5 px-5 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(draft);
            }}
          >
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Name">
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Manager"
                  maxLength={60}
                  required
                  disabled={draft.isBuiltin}
                />
              </Field>
              <Field label="Description">
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Sees players and counts; ids and usernames masked"
                  maxLength={200}
                />
              </Field>
            </div>
            <PermissionMatrix
              kind="role"
              value={draft.permissions}
              onChange={(permissions) => setDraft({ ...draft, permissions })}
              limit={me.role === "super_admin" ? null : me.permissions}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={save.isPending || !draft.name.trim()}>
                {save.isPending ? "Saving" : draft.id === null ? "Create role" : "Save role"}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setDraft(null)}>
                Cancel
              </Button>
            </div>
            {save.error && <ErrorNote error={save.error} />}
          </form>
        </Panel>
      )}

      <Panel
        title="Roles"
        description="Give each admin a role, then adjust one person with exceptions on the Admins page."
        action={
          canManage &&
          !draft && (
            <Button
              size="sm"
              onClick={() =>
                open({
                  id: null,
                  name: "",
                  description: "",
                  permissions: NOTHING,
                  isBuiltin: false,
                })
              }
            >
              <Plus className="size-4" /> New role
            </Button>
          )
        }
      >
        {roles.isLoading ? (
          <Loading />
        ) : roles.error ? (
          <div className="p-5">
            <ErrorNote error={roles.error} />
          </div>
        ) : roles.data && roles.data.length === 0 ? (
          <Empty title="No roles yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Role</Th>
                <Th className="w-28">People</Th>
                <Th className="w-40" />
              </tr>
            </thead>
            <tbody>
              {roles.data?.map((role) => (
                <tr key={role.id}>
                  <Td primary>
                    <span className="grid gap-0.5">
                      <span className="flex items-center gap-2 font-medium">
                        {role.name}
                        {role.isBuiltin && <Status value="builtin" label="Built-in" />}
                      </span>
                      {role.description && (
                        <span className="text-muted-foreground">{role.description}</span>
                      )}
                    </span>
                  </Td>
                  <Td label="People">{role.members}</Td>
                  <Td>
                    {canManage && (
                      <span className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={() =>
                            open({
                              id: role.id,
                              name: role.name,
                              description: role.description ?? "",
                              permissions: role.permissions,
                              isBuiltin: role.isBuiltin,
                            })
                          }
                        >
                          Edit
                        </Button>
                        {!role.isBuiltin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={remove.isPending || role.members > 0}
                            title={
                              role.members > 0 ? "Move everyone off this role first" : undefined
                            }
                            onClick={async () => {
                              if (
                                await confirm({
                                  title: `Delete the role “${role.name}”?`,
                                  confirmLabel: "Delete role",
                                  destructive: true,
                                })
                              )
                                remove.mutate(role.id);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {remove.error && (
          <div className="p-5">
            <ErrorNote error={remove.error} />
          </div>
        )}
      </Panel>
    </div>
  );
}
